// Контролите на десния панел в „Конфигурация на маси" (по макета): заглавие
// с kicker, сегментиран превключвател, поле, места (стъпер на цяла ширина +
// бързи бутони), избор на зала, бутоните. Само Tailwind, семантични токени —
// тъмната тема идва от тях.

import type { ReactNode, Ref } from "react";
import { CloseOutlined, DownOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import type { SchemaRegion } from "../../../api/restaurantSchema";
import { MAX_SEATS } from "../../../components/restaurant-schema/geometry";
import { FOCUS_RING, SEAT_CHIPS } from "./tablesPanelStyles";

/** Kicker + заглавие на панела; `onClose` добавя „Затвори" (×) вдясно. */
export const PanelHeading = ({
  kicker,
  title,
  onClose,
}: {
  kicker: string;
  title: string;
  onClose?: () => void;
}) => {
  const { t } = useTranslation();
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <div className="text-[10.5px] font-bold uppercase leading-[1.2] tracking-[1.2px] text-muted">
          {kicker}
        </div>
        <div className="mt-1.5 truncate text-[17px] font-bold leading-[1.25] tracking-[-0.3px] text-ink">
          {title}
        </div>
      </div>
      {onClose && (
        <button
          type="button"
          onClick={onClose}
          aria-label={t("tablesConfig.edit.close")}
          title={t("tablesConfig.edit.close")}
          className="-mr-1.5 -mt-1 flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-lg border-none bg-transparent text-[14px] text-muted transition-colors duration-150 hover:bg-shade-1 hover:text-ink"
        >
          <CloseOutlined />
        </button>
      )}
    </div>
  );
};

/** Етикет над поле (uppercase eyebrow) + по избор грешка/подсказка отдолу. */
export const PanelField = ({
  label,
  error,
  hint,
  children,
}: {
  label: string;
  error?: string | null;
  hint?: string | null;
  children: ReactNode;
}) => (
  <div className="flex flex-col gap-2">
    <div className="text-[10.5px] font-bold uppercase leading-[1.2] tracking-[1.2px] text-muted">{label}</div>
    {children}
    {error ? (
      <div role="alert" className="text-[12px] leading-[1.45] text-danger-ink">
        {error}
      </div>
    ) : (
      hint && <div className="text-[12px] leading-[1.45] text-muted">{hint}</div>
    )}
  </div>
);

/** Сегментиран превключвател: сива лента, избраният сегмент е бял и вдигнат. */
export const SegmentedSwitch = <Value extends string>({
  options,
  value,
  onChange,
}: {
  options: { value: Value; label: string }[];
  value: Value;
  onChange: (value: Value) => void;
}) => (
  <div role="tablist" className="grid auto-cols-fr grid-flow-col gap-1 rounded-[11px] bg-fill-2 p-1">
    {options.map((option) => {
      const active = option.value === value;
      return (
        <button
          key={option.value}
          type="button"
          role="tab"
          aria-selected={active}
          onClick={() => onChange(option.value)}
          className={`h-9 cursor-pointer rounded-[8px] border-none px-2 text-[13px] leading-[1.2] transition-colors duration-150 ${
            active
              ? "bg-surface font-bold text-ink shadow-[0_1px_3px_rgba(28,25,23,0.14)]"
              : "bg-transparent font-semibold text-body hover:text-ink"
          }`}
        >
          {option.label}
        </button>
      );
    })}
  </div>
);

/** Текстово/числово поле на панела (16px на телефон — иначе iOS зуумва). */
export const PanelInput = ({
  value,
  onChange,
  onEnter,
  onEscape,
  inputRef,
  ariaLabel,
  placeholder,
  invalid,
  numeric,
}: {
  value: string;
  onChange: (value: string) => void;
  onEnter?: () => void;
  // Връща стойността; Esc не стига до глобалното „назад", нито до панела.
  // Подавай го САМО когато има какво да се върне — иначе Esc се поглъща и
  // панелът/sheet-ът не може да се затвори, докато полето е на фокус.
  onEscape?: () => void;
  inputRef?: Ref<HTMLInputElement>;
  ariaLabel: string;
  placeholder?: string;
  invalid?: boolean;
  numeric?: boolean;
}) => (
  <input
    ref={inputRef}
    value={value}
    aria-label={ariaLabel}
    aria-invalid={invalid ? true : undefined}
    placeholder={placeholder}
    maxLength={50}
    inputMode={numeric ? "numeric" : undefined}
    onChange={(event) => onChange(event.target.value)}
    onKeyDown={(event) => {
      if (event.key === "Enter" && onEnter) {
        event.preventDefault();
        onEnter();
      } else if (event.key === "Escape" && onEscape) {
        event.preventDefault();
        onEscape();
      }
    }}
    className={`h-11 w-full min-w-0 rounded-[10px] border bg-surface px-3.5 text-[16px] font-semibold tabular-nums text-ink outline-none transition-[border-color,box-shadow] duration-150 placeholder:font-medium placeholder:text-faint sm:text-[15px] ${
      invalid
        ? "border-danger focus:shadow-[0_0_0_3px_color-mix(in_srgb,var(--color-danger)_15%,transparent)]"
        : `border-shade-3 ${FOCUS_RING}`
    }`}
  />
);

/** Места: − N + на цяла ширина и бързите бутони (избраният е плътен кехлибарен). */
export const SeatsPicker = ({
  value,
  onChange,
  disabled,
}: {
  value: number;
  onChange: (value: number) => void;
  disabled?: boolean;
}) => {
  const { t } = useTranslation();
  const stepButton =
    "flex h-full w-12 cursor-pointer items-center justify-center border-none bg-transparent text-[20px] text-ink-2 transition-colors duration-150 hover:bg-fill disabled:cursor-not-allowed disabled:opacity-35";
  return (
    <div className="flex flex-col gap-2">
      <div className="flex h-11 items-center overflow-hidden rounded-[10px] border border-shade-3 bg-surface">
        <button
          type="button"
          aria-label={t("tablesConfig.seatsLess")}
          disabled={disabled || value <= 1}
          onClick={() => onChange(Math.max(1, value - 1))}
          className={stepButton}
        >
          −
        </button>
        <span className="flex-1 text-center text-[16px] font-bold tabular-nums text-ink">{value}</span>
        <button
          type="button"
          aria-label={t("tablesConfig.seatsMore")}
          disabled={disabled || value >= MAX_SEATS}
          onClick={() => onChange(Math.min(MAX_SEATS, value + 1))}
          className={stepButton}
        >
          +
        </button>
      </div>
      <div className="grid grid-cols-6 gap-1.5">
        {SEAT_CHIPS.map((count) => {
          const selected = count === value;
          return (
            <button
              key={count}
              type="button"
              disabled={disabled}
              aria-pressed={selected}
              onClick={() => onChange(count)}
              className={`h-9 cursor-pointer rounded-[9px] text-[13.5px] font-semibold tabular-nums transition-colors duration-150 disabled:cursor-not-allowed disabled:opacity-50 ${
                selected
                  ? "border border-brand bg-brand text-white"
                  : "border border-shade-3 bg-surface text-ink-2 hover:bg-fill"
              }`}
            >
              {count}
            </button>
          );
        })}
      </div>
    </div>
  );
};

/** Избор на зала (роден <select> — на телефон отваря системния избор). */
export const RegionSelect = ({
  regions,
  value,
  onChange,
  placeholder,
  ariaLabel,
  disabled,
}: {
  regions: SchemaRegion[];
  value: number | null;
  onChange: (region: SchemaRegion) => void;
  placeholder?: string;
  ariaLabel: string;
  disabled?: boolean;
}) => (
  <div className="relative">
    <select
      value={value ?? ""}
      aria-label={ariaLabel}
      disabled={disabled}
      onChange={(event) => {
        const target = regions.find((region) => region.id === Number(event.target.value));
        if (target) onChange(target);
      }}
      className={`h-11 w-full cursor-pointer appearance-none rounded-[10px] border border-shade-3 bg-surface pl-3.5 pr-9 text-[16px] font-semibold text-ink outline-none transition-[border-color,box-shadow] duration-150 disabled:cursor-not-allowed disabled:opacity-50 sm:text-[14px] ${FOCUS_RING}`}
    >
      {value === null && (
        <option value="" disabled>
          {placeholder}
        </option>
      )}
      {regions.map((region) => (
        <option key={region.id} value={region.id}>
          {region.name}
        </option>
      ))}
    </select>
    <DownOutlined className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-[11px] text-muted" />
  </div>
);

/** Тънък разделител между секциите на панела. */
export const PanelDivider = () => <div className="border-t border-shade-2" />;
